import React, { useEffect, useState } from "react";
import {
  updateTournamentTeams,
  updateTournament,
} from "services/tournamentService";
import { fetchUsersByIds } from "services/userService";
import { generateTournamentTeams } from "services/tournamentService";
import * as Sentry from "@sentry/react";

const TournamentTeamManager = ({
  tournament,
  locationId,
  registrations,
  locationRankings = [],
}) => {
  const [unrankedUsers, setUnrankedUsers] = useState([]);
  const [isOddRegistrations, setIsOddRegistrations] = useState(false);
  const [division1TeamsCount, setDivision1TeamsCount] = useState(0);
  const [division2TeamsCount, setDivision2TeamsCount] = useState(0);
  const [makeDivisions, setMakeDivisions] = useState(false);

  const maxTotalTeams = Math.floor(
    Math.min(registrations.length, tournament?.waitingListLimit ?? Infinity) /
      (tournament?.type === "Simple" || tournament.type === "Double Open"
        ? 1
        : 2)
  );

  useEffect(() => {
    if (registrations && Array.isArray(registrations) && tournament) {
      const effectiveLimit = Math.min(
        registrations.length,
        tournament.waitingListLimit ?? Infinity
      );
      const total = Math.floor(
        effectiveLimit /
          (tournament?.type === "Simple" || tournament.type === "Double Open"
            ? 1
            : 2)
      );
      setDivision1TeamsCount(total);
      setMakeDivisions(
        tournament.makeDivisions ?? false
      );
    }
  }, [registrations, tournament]);

  useEffect(() => {
    if (!tournament || !registrations) return;

    const checkPlayers = async () => {
      // 🕒 Step 1: Parse date strings and sort
      const sortedRegs = registrations
        .filter((reg) => reg.createdAt)
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

      // 🎯 Step 2: Limit to waitingListLimit
      const limitedRegs = sortedRegs.slice(0, tournament.waitingListLimit);

      // 🤹 Step 3: Set odd player logic
      setIsOddRegistrations(
        tournament.type === "DYP" &&
          limitedRegs.length % 2 !== 0 &&
          !(
            limitedRegs.length > tournament.waitingListLimit &&
            tournament.waitingListLimit % 2 === 0
          )
      );

      const userIds = limitedRegs.flatMap((reg) =>
        [reg.userId, reg.teamMateId].filter(Boolean)
      );

      // 🧠 Step 5: Fetch user info
      const usersFromDB = await fetchUsersByIds(userIds);

      // 🧼 Step 6: Filter unranked users
      const unranked = usersFromDB.filter(
        (user) => !locationRankings.some((lr) => lr.userId === user.id)
      );

      // ✅ Step 7: Update state
      setUnrankedUsers(unranked);
    };

    checkPlayers();
  }, [tournament, locationId, registrations, locationRankings]);

  const handleCreateTeams = async () => {
    try {
      await generateTournamentTeams({
        tournamentId: tournament.id,
        registrations,
        locationRankings,
        division1TeamsCount,
        division2TeamsCount,
      });
      window.location.reload(); // or use state if you prefer
    } catch (error) {
      Sentry.captureException(error);
      alert(error.message);
      console.error("Erreur lors de la création des équipes :", error);
    }
  };

  const handleDeleteTeams = async () => {
    try {
      await updateTournamentTeams(tournament.id, {});
      window.location.reload(); // ✅ refresh after deletion
    } catch (error) {
      Sentry.captureException(error);
      console.error("Erreur lors de la suppression des équipes :", error);
    }
  };

  if (!tournament)
    return <div className="text-white">Chargement du tournoi...</div>;

  return (
    <div className="text-white p-4 rounded-md">
      {unrankedUsers.length > 0 ? (
        <div>
          <h3 className="font-bold text-lg mb-2">Joueurs sans classement :</h3>
          <ul className="list-disc pl-5 text-red-400">
            {unrankedUsers.map((user) => (
              <li key={user.id}>{user.name}</li>
            ))}
          </ul>
        </div>
      ) : isOddRegistrations ? (
        <p className="text-yellow-300">
          ⚠️ Le nombre d'inscriptions est impair. Veuillez ajuster le nombre de
          joueurs.
        </p>
      ) : (
        <>
          {tournament.teams?.division1?.length ||
          tournament.teams?.division2?.length ? (
            <button
              onClick={handleDeleteTeams}
              className="bg-red-600 hover:bg-red-500 text-white font-bold px-4 py-2 rounded-md"
            >
              Supprimer les équipes
            </button>
          ) : (
            <>
              <div className="flex gap-4 flex-wrap mb-4">
                <div>
                  <label className="block mb-1 text-sm">Équipes pool 1</label>
                  <input
                    type="number"
                    min="0"
                    max={maxTotalTeams}
                    value={division1TeamsCount}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10) || 0;
                      const remaining = maxTotalTeams - val;
                      setDivision1TeamsCount(val);
                      if (val + division2TeamsCount > maxTotalTeams) {
                        setDivision2TeamsCount(remaining);
                      }
                    }}
                    className="bg-gray-700 border border-gray-500 rounded px-2 py-1 w-20"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm">Équipes pool 2</label>
                  <input
                    type="number"
                    min="0"
                    max={maxTotalTeams}
                    value={division2TeamsCount}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10) || 0;
                      const remaining = maxTotalTeams - val;
                      setDivision2TeamsCount(val);
                      if (val + division1TeamsCount > maxTotalTeams) {
                        setDivision1TeamsCount(remaining);
                      }
                    }}
                    className="bg-gray-700 border border-gray-500 rounded px-2 py-1 w-20"
                  />
                </div>
              </div>
              <div className="flex items-center mb-4">
                <input
                  id="makeDivisions"
                  type="checkbox"
                  checked={makeDivisions}
                  onChange={async (e) => {
                    const checked = e.target.checked;
                    setMakeDivisions(checked);

                    try {
                      await updateTournament(tournament.id, {
                        makeDivisions: checked,
                      });
                    } catch (err) {
                      Sentry.captureException(err);
                      console.error("❌ Failed to update makeDivisions:", err);
                    }
                  }}
                  className="form-checkbox h-5 w-5 text-amber-500 mr-2"
                />
                <label htmlFor="makeDivisions" className="text-md">
                  {"Faire 2 divisions"}
                </label>
              </div>
              <button
                onClick={handleCreateTeams}
                className="bg-green-600 hover:bg-green-500 text-white font-bold px-4 py-2 rounded-md"
              >
                Faire les équipes!
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default TournamentTeamManager;
